import { CSSObject } from "@emotion/react";
import { OptionItem } from "@interface48/api";
import Chip from "@material-ui/core/Chip";
import Input from "@material-ui/core/Input";
import ListSubheader from "@material-ui/core/ListSubheader";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import { createStyles, Theme, withStyles, WithStyles } from "@material-ui/core/styles";
import { emphasize } from "@material-ui/core/styles/colorManipulator";
import Typography from "@material-ui/core/Typography";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ClearIcon from "@material-ui/icons/Clear";
import classNames from "classnames";
import React from "react";
import { SelectComponentsConfig } from "react-select/src/components";
import { ValueContainerProps } from "react-select/src/components/containers";
import { ControlProps } from "react-select/src/components/Control";
import { GroupProps } from "react-select/src/components/Group";
import { IndicatorProps } from "react-select/src/components/indicators";
import { MenuProps, NoticeProps } from "react-select/src/components/Menu";
import { MultiValueProps } from "react-select/src/components/MultiValue";
import { OptionProps } from "react-select/src/components/Option";
import { PlaceholderProps } from "react-select/src/components/Placeholder";
import { SingleValueProps } from "react-select/src/components/SingleValue";

type OptionType = OptionItem<string | number>;
type GroupType = { label: string; options: OptionType[] };

export const getMuiStyles = (theme: Theme) => {
  return {
    container: (base: CSSObject): CSSObject => ({
      ...base,
      flexGrow: 1,
    }),
    control: (base: CSSObject): any => ({
      ...base,
      display: "flex",
      alignItems: "flex-start",
      whitespace: "nowrap",
      textOverflow: "ellipsis",
      border: 0,
      height: "auto",
      background: "transparent",
      "&:hover": {
        boxShadow: "none",
      },
    }),
    input: (base: CSSObject): CSSObject => ({
      ...base,
      display: "flex",
      margin: 0,
      color: theme.palette.text.primary,
    }),
    divider: (base: CSSObject): CSSObject => ({
      ...base,
      height: theme.spacing(2),
    }),
    menuPortal: (base: CSSObject): CSSObject => ({
      ...base,
      zIndex: 1500,
    }),
    menu: (base: CSSObject): CSSObject => ({
      ...base,
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[2],
      position: "absolute",
      left: 0,
      top: "100%",
      width: "100%",
    }),
    menuList: (base: CSSObject): CSSObject => ({
      ...base,
      overflowY: "auto",
      paddingTop: 0,
      paddingBottom: 0,
    }),
    indicatorsContainer: (base: CSSObject): CSSObject => ({
      ...base,
      alignItems: "flex-end",
      paddingBottom: 4,
    }),
    indicatorSeparator: (base: CSSObject): CSSObject => ({
      ...base,
      display: "none",
    }),
  };
};

const controlStyles = (theme: Theme) =>
  createStyles({
    root: {
      cursor: "pointer",
      fontSize: "inherit",
    },
    input: {
      display: "flex",
      margin: 0,
      height: "auto",
      padding: 0,
      "& input": {
        fontFamily: theme.typography.fontFamily,
        fontSize: "inherit",
      },
    },
  });

const InputComponent = (props: any) => {
  const { inputRef, ...rest } = props;

  return <div ref={inputRef} {...rest} />;
};

const Control = withStyles(controlStyles)(
  React.forwardRef((props: ControlProps<OptionType, boolean, GroupType> & WithStyles<typeof controlStyles>, ref) => {
    const { selectProps, children, innerProps, classes } = props;
    const { value, isMulti } = selectProps;

    let inputValue: string = "";

    if (isMulti) {
      const multipleOptions = value as OptionType[];
      inputValue = (multipleOptions && multipleOptions.map(v => v.value.toString()).join(",")) || "";
    } else {
      const singleOption = value as OptionType;
      inputValue = (singleOption && singleOption.value.toString()) || "";
    }

    return (
      <Input
        fullWidth={true}
        value={inputValue}
        inputComponent={InputComponent}
        inputProps={{ inputRef: ref, children, ...innerProps }}
        classes={{ root: classes.root, input: classes.input }}
      />
    );
  }),
);

const menuStyles = (theme: Theme) =>
  createStyles({
    root: {
      position: "absolute",
      width: "100%",
      zIndex: 3,
    },
  });

const Menu = withStyles(menuStyles)(
  React.forwardRef((props: MenuProps<OptionType, boolean, GroupType> & WithStyles<typeof menuStyles>, ref) => {
    const { children, innerProps, classes } = props;

    return (
      <Paper ref={ref} square={true} {...innerProps} classes={{ root: classes.root }}>
        {children}
      </Paper>
    );
  }),
);

const groupStyles = (theme: Theme) =>
  createStyles({
    listSubheader: {
      backgroundColor: theme.palette.background.paper,
    },
  });

const Group = withStyles(groupStyles)(
  (props: GroupProps<OptionType, boolean, GroupType> & WithStyles<typeof groupStyles>) => {
    const { label, children, classes } = props;

    return (
      <>
        <ListSubheader className={classes.listSubheader}>{label}</ListSubheader>
        <div>{children}</div>
      </>
    );
  },
);

const Option = React.forwardRef((props: OptionProps<OptionType, boolean, GroupType>, ref) => {
  const { isFocused, isSelected, children, innerProps } = props;

  return (
    <MenuItem component="div" ref={ref as any} selected={isFocused || isSelected} {...innerProps}>
      {children}
    </MenuItem>
  );
});

const valueContainerStyles = (theme: Theme) =>
  createStyles({
    valueContainer: {
      display: "flex",
      flex: 1,
      flexWrap: "wrap",
      alignItems: "center",
      padding: "5px 0 4px 0",
      overflow: "hidden",
    },
    multiValueContainer: {
      padding: "3px 0 6px 0",
    },
  });

const ValueContainer = withStyles(valueContainerStyles)(
  (props: ValueContainerProps<OptionType, boolean, GroupType> & WithStyles<typeof valueContainerStyles>) => {
    const { children, classes } = props;

    const className = classNames(classes.valueContainer, props.isMulti ? classes.multiValueContainer : undefined);

    return <div className={className}>{children}</div>;
  },
);

const singleValueStyles = (theme: Theme) =>
  createStyles({
    singleValue: {
      display: "flex",
      alignItems: "center",
      fontFamily: theme.typography.fontFamily,
      fontSize: "inherit",
    },
  });

const SingleValue = withStyles(singleValueStyles)(
  (props: SingleValueProps<OptionType, GroupType> & WithStyles<typeof singleValueStyles>) => {
    const { children, innerProps, classes } = props;

    return (
      <Typography className={classes.singleValue} {...innerProps}>
        {children}
      </Typography>
    );
  },
);

const multiValueStyles = (theme: Theme) =>
  createStyles({
    chip: {
      margin: "4px 4px 0 0",
      height: 24,
    },
    chipFocused: {
      backgroundColor: emphasize(
        theme.palette.type === "light" ? theme.palette.grey[300] : theme.palette.grey[700],
        0.08,
      ),
    },
    chipDeleteIcon: {
      marginRight: 2,
    },
  });

const MultiValue = withStyles(multiValueStyles)(
  (props: MultiValueProps<OptionType, GroupType> & WithStyles<typeof multiValueStyles>) => {
    const { isFocused, children, innerProps, removeProps, classes } = props;

    const onDelete = (event: Event) => {
      removeProps.onClick(event);
      removeProps.onMouseDown(event);
    };

    return (
      <Chip
        tabIndex={-1}
        label={children}
        classes={{
          root: classNames(classes.chip, { [classes.chipFocused]: isFocused }),
          deleteIcon: classes.chipDeleteIcon,
        }}
        onDelete={onDelete}
        {...innerProps}
      />
    );
  },
);

const placeholderStyles = (theme: Theme) =>
  createStyles({
    placeholder: {
      fontFamily: theme.typography.fontFamily,
      fontSize: "inherit",
    },
  });

const Placeholder = withStyles(placeholderStyles)(
  (props: PlaceholderProps<OptionType, boolean, GroupType> & WithStyles<typeof placeholderStyles>) => {
    // const { classes, innerProps, children } = props;

    // return (
    //   <Typography color="textSecondary" className={classes.placeholder} {...innerProps}>
    //     {children}
    //   </Typography>
    // );
    return null; // Do not use Placeholder at this time
  },
);

const noOptionsMessageStyles = (theme: Theme) =>
  createStyles({
    message: {
      padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    },
  });

const NoOptionsMessage = withStyles(noOptionsMessageStyles)(
  (props: NoticeProps<OptionType, boolean, GroupType> & WithStyles<typeof noOptionsMessageStyles>) => {
    const { children, innerProps, classes } = props;

    return (
      <Typography color="textSecondary" className={classes.message} {...innerProps}>
        {children}
      </Typography>
    );
  },
);

const indicatorStyles = (theme: Theme) =>
  createStyles({
    icon: {
      color: theme.palette.text.secondary,
      zIndex: 1,
    },
  });

const ClearIndicator = withStyles(indicatorStyles)(
  (props: IndicatorProps<OptionType, boolean, GroupType> & WithStyles<typeof indicatorStyles>) => {
    const { clearValue, innerProps, classes } = props;

    const handleClick = (event: any) => {
      clearValue();
    };

    return <ClearIcon className={classes.icon} onClick={handleClick} {...innerProps} />;
  },
);

const DropdownIndicator = withStyles(indicatorStyles)(
  (props: IndicatorProps<OptionType, boolean, GroupType> & WithStyles<typeof indicatorStyles>) => {
    const { innerProps, classes } = props;

    return <ArrowDropDownIcon className={classes.icon} {...innerProps} />;
  },
);

export const MuiComponents = {
  Control,
  Menu,
  Group,
  Option,
  ValueContainer,
  SingleValue,
  MultiValue,
  Placeholder,
  NoOptionsMessage,
  ClearIndicator,
  DropdownIndicator,
} as SelectComponentsConfig<OptionType, boolean, GroupType>;
